import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMarketingCampaignList } from '../../../network/campaignCurd';
import { getColumns } from './util/list.util';
import { CampaignElement, CampaignStatusEnum } from '../../../types/campaign-types';
import { useHistory } from 'react-router-dom';
import { MARKETING_DETAIL_PATH } from '../marketing-campaign-routes';
import { queryData, generateCompareFunction } from '../../../util/campaign-list.util';
import { FilterState } from 'src/app/common/components/ProTable/ProFilter';
import { useStyles } from './list.style';
import { updateMenuBadge } from 'src/redux/common/commonSlice';
import { UnreadCampaignMap } from 'src/app/common/components/layout/components/aside/components/nested-menu';

interface HookProps {
  cacheParams?: React.MutableRefObject<Record<string, any> | undefined>;
  // other params
}

export const useList = ({ cacheParams }: HookProps) => {
  const intl = useIntl();
  const { menuBadge } = useSelector((state: any) => state.common);
  const Translation = (id: string) => intl.formatMessage({ id });
  const history = useHistory();
  const [keyword, setKeyword] = useState('');
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const styles = useMemo(() => classes, [classes]);
  const columns = useMemo(
    () =>
      getColumns({
        t: Translation,
        keyword,
        styles,
      }),
    [Translation, keyword, styles],
  );
  const getData = useCallback(
    async (
      params: {
        page: number;
        pageSize: number;
        current?: number | undefined;
        keyword?: string | undefined;
      },
      sort: { [key: string]: any },
      filter?: { [key: string]: any },
    ): Promise<any> => {
      const res = await fetchMarketingCampaignList({
        name: params.keyword ?? '',
        statusArray: [CampaignStatusEnum.active, CampaignStatusEnum.completed],
      });
      let data = res.campaigns;
      if (sort) {
        const compareFunction = generateCompareFunction(sort);
        data.sort(compareFunction);
      }
      data = data.slice(params.page * params.pageSize, (params.page + 1) * params.pageSize);
      if (res) {
        return {
          success: true,
          data: data,
          total: res.totalDocs,
        };
      } else {
        return {
          success: false,
          data: [],
          total: 0,
        };
      }
    },
    [],
  );

  const onFilterChange = useCallback(
    (filterState: FilterState) => {
      const { keyword } = filterState;
      cacheParams && (cacheParams.current = filterState);
      setKeyword(keyword);
    },
    [cacheParams],
  );

  const toDetail = (e: CampaignElement) => {
    history.push(`${MARKETING_DETAIL_PATH}/${e._id}`);

    !e.lastViewedTime &&
      menuBadge[UnreadCampaignMap.MARKETING_CAMPAIGN] &&
      dispatch(
        updateMenuBadge({
          ...menuBadge,
          [UnreadCampaignMap.MARKETING_CAMPAIGN]: menuBadge[UnreadCampaignMap.MARKETING_CAMPAIGN] - 1,
        }),
      );
  };

  return {
    getData,
    columns,
    params: {},
    toDetail,
    onFilterChange,
  };
};
